import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Separator } from "./separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";

const singleSelectVariants = cva(
  "m-0",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface SingleSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof singleSelectVariants> {
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  selectedValue: string | null;
  setSelectedValue: React.Dispatch<React.SetStateAction<string | null>>;
  onValueChange: (value: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}

export const SingleSelectToCountry = React.forwardRef<
  HTMLButtonElement,
  SingleSelectProps
>(
  (
    {
      options,
      selectedValue,
      setSelectedValue,
      onValueChange,
      placeholder = "Select option",
      className,
      disabled = false,
      loading = false,
      variant,
      ...props
    },
    ref
  ) => {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    const selectedOption = options.find((opt) => opt.value === selectedValue);

    const handleSelect = (value: string) => {
      setSelectedValue(value);
      onValueChange(value);
      setIsPopoverOpen(false);
    };

    const handleClear = () => {
      setSelectedValue(null);
      onValueChange(null);
    };

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            disabled={disabled}
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
            className={cn(
    "flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit",
    singleSelectVariants({ variant }),
    className
  )}
          >
            {selectedOption ? (
              <div className="flex justify-between w-full items-center">
                <div className="flex items-center gap-2 ml-2 text-sm text-[#64748B] font-normal">
                  {selectedOption.icon && (
                    <selectedOption.icon className="h-4 w-4 text-muted-foreground" />
                  )}
                  {selectedOption.label}
                </div>
                <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-sm text-muted-foreground mx-3">{placeholder}</span>
                <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[545px] p-0 " align="start">
          <Command>
            <CommandInput placeholder="Search..." />
            <CommandList className="max-h-[200px] overflow-y-auto">
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {/* {loading ? (
                  <CommandItem className="justify-center">Loading…</CommandItem>
                ) : (
                  options.map((option) => (
                    <CommandItem
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                      className="cursor-pointer"
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-3 w-3 items-center justify-center rounded-full border border-primary",
                          selectedValue === option.value
                            ? "bg-primary border-primary"
                            : "opacity-50"
                        )}
                      >
                        {selectedValue === option.value && (
                          <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                        )}
                      </div>
                      {option.icon && (
                        <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                      )}
                      {option.label}
                    </CommandItem>
                  ))
                )} */}

                {loading ? (
  <CommandItem className="justify-center">Loading…</CommandItem>
) : (
  <>
    {/* Selected option first */}
    {selectedOption && (
      <CommandItem
        key={selectedOption.value}
        onSelect={() => handleSelect(selectedOption.value)}
        className="cursor-pointer bg-accent/50"
      >
        <div className="mr-2 flex h-3 w-3 items-center justify-center rounded-full border border-primary bg-primary">
          <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
        </div>
        {selectedOption.icon && (
          <selectedOption.icon className="mr-2 h-4 w-4 text-muted-foreground" />
        )}
        {selectedOption.label}
      </CommandItem>
    )}
    
    {/* Separator if there's a selected option */}
    {/* {selectedOption && <Separator className="my-1" />} */}
    
    {/* All other options */}
    {options
      .filter(option => option.value !== selectedValue)
      .map((option) => (
        <CommandItem
          key={option.value}
          onSelect={() => handleSelect(option.value)}
          className="cursor-pointer"
        >
          <div className="mr-2 flex h-3 w-3 items-center justify-center rounded-full border border-primary opacity-50">
          </div>
          {option.icon && (
            <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
          )}
          {option.label}
        </CommandItem>
      ))}
  </>
)}
              </CommandGroup>
              {/* {selectedValue && (
                <>
                  <Separator className="my-1" />
                  <CommandGroup>
                    <CommandItem
                      onSelect={handleClear}
                      className="justify-center cursor-pointer"
                    >
                      Clear Selection
                    </CommandItem>
                  </CommandGroup>
                </>
              )} */}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

SingleSelectToCountry.displayName = "SingleSelectToCountry";